import { Box, Button, Chip, TextField, Typography } from '@mui/material';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const VideoContentSection = ({
    video,
    formik,
    showFileInput,
    showLinkInput,
    setShowFileInput,
    setShowLinkInput,
    uploadVideo,
    deleteVideo,
    handleFormChange,
    theme
}) => {
    /**
     * Derives a display-friendly filename from the video value.
     * Video can be either a string URL or a File object.
     */
    const getVideoDisplayName = () => {
        if (!video) return '';
        if (typeof video === 'string') {
            return video.substring(video.lastIndexOf('/') + 1);
        }
        return video?.name || '';
    };

    /**
     * Returns status banner styling based on current content state.
     */
    const getStatusConfig = () => {
        if (video) {
            return {
                borderColor: '#A5D6A7',
                backgroundColor: 'rgba(232, 245, 233, 0.6)',
                iconColor: '#2E7D32',
                titleColor: '#1B5E20',
                captionColor: '#2E7D32',
                title: 'Active Content: Uploaded MP4 Video',
                caption: getVideoDisplayName(),
                Icon: OndemandVideoIcon
            };
        }
        if (formik.values.externalLink) {
            return {
                borderColor: '#90CAF9',
                backgroundColor: 'rgba(227, 242, 253, 0.6)',
                iconColor: '#1565C0',
                titleColor: '#0D47A1',
                captionColor: '#1565C0',
                title: 'Active Content: External Video Link',
                caption: formik.values.externalLink,
                Icon: PlayCircleOutlineIcon
            };
        }
        return {
            borderColor: '#FFCC80',
            backgroundColor: 'rgba(255, 243, 224, 0.6)',
            iconColor: '#E65100',
            titleColor: '#E65100',
            captionColor: '#EF6C00',
            title: 'No Active Content',
            caption:
                'Please configure either an uploaded MP4 file or an external video link using the selectors below.',
            Icon: InfoOutlinedIcon
        };
    };

    const status = getStatusConfig();
    const StatusIcon = status.Icon;

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 600,
                    color: 'text.secondary'
                }}
            >
                Lecture Video
            </Typography>

            {/* Current Content Status Banner */}
            <Box
                sx={{
                    p: 2,
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: status.borderColor,
                    backgroundColor: status.backgroundColor,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 1
                }}
            >
                <StatusIcon
                    sx={{
                        color: status.iconColor,
                        fontSize: '1.8rem'
                    }}
                />
                <Box>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 700,
                            color: status.titleColor
                        }}
                    >
                        {status.title}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            color: status.captionColor,
                            display: 'block',
                            wordBreak: 'break-all'
                        }}
                    >
                        {status.caption}
                    </Typography>
                </Box>
            </Box>

            {/* Toggle Buttons */}
            <Box display="flex" gap={2} mb={1}>
                <Button
                    variant={showFileInput ? 'contained' : 'outlined'}
                    color="secondary"
                    onClick={() => {
                        setShowFileInput(true);
                        setShowLinkInput(false);
                    }}
                    sx={{
                        fontWeight: 'bold',
                        textTransform: 'none',
                        borderRadius: '8px'
                    }}
                >
                    Upload MP4 File
                </Button>
                <Button
                    variant={showLinkInput ? 'contained' : 'outlined'}
                    color="secondary"
                    onClick={() => {
                        setShowFileInput(false);
                        setShowLinkInput(true);
                    }}
                    sx={{
                        fontWeight: 'bold',
                        textTransform: 'none',
                        borderRadius: '8px'
                    }}
                >
                    External Link
                </Button>
            </Box>

            {/* File Upload Zone */}
            {showFileInput && (
                <Box
                    sx={{
                        border: '2px dashed',
                        borderColor: video
                            ? theme.palette.secondary.main
                            : theme.palette.divider,
                        borderRadius: '12px',
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: video
                            ? 'rgba(82, 146, 221, 0.02)'
                            : theme.palette.grey[50],
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            borderColor: theme.palette.secondary.main,
                            backgroundColor: 'rgba(82, 146, 221, 0.04)'
                        }
                    }}
                >
                    <input
                        type="file"
                        accept="video/*"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            opacity: 0,
                            cursor: 'pointer',
                            zIndex: 2
                        }}
                        onChange={uploadVideo}
                    />
                    {video ? (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            gap={1.5}
                        >
                            <Chip
                                label={getVideoDisplayName()}
                                onDelete={deleteVideo}
                                color="secondary"
                                sx={{
                                    fontWeight: 600,
                                    zIndex: 3,
                                    position: 'relative'
                                }}
                            />
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Click or drag file to replace video
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            gap={1}
                        >
                            <OndemandVideoIcon
                                sx={{
                                    fontSize: '2.5rem',
                                    color: theme.palette.text.secondary,
                                    opacity: 0.7
                                }}
                            />
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 600 }}
                            >
                                Click or drag video file to upload
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Supports standard formats MP4, WebM (Max 100MB)
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* External Link Input */}
            {showLinkInput && (
                <Box>
                    <TextField
                        label="External Video Link (YouTube/Vimeo)"
                        fullWidth
                        value={formik.values.externalLink}
                        name="externalLink"
                        variant="outlined"
                        placeholder="https://www.youtube.com/watch?v=..."
                        onChange={(e) => handleFormChange(e)}
                        helperText={formik.errors.externalLink}
                        error={formik.errors.externalLink}
                    />
                </Box>
            )}
        </Box>
    );
};

export default VideoContentSection;
